import React, { useEffect, useCallback, useState } from "react";
import { useDateQuery } from '@ls/date-module';
import { useDispatch, useStore, useSelector, batch } from "react-redux";
import Dialog from "@ls/disclaimer-service";
import { useShowPrompt } from "@ls/react-prompt";
//import { useGetDate } from "@ls/date-module";
import { Provider as GridStateLegacyProvider } from "@ls/grid-state-module";
import { Provider as GridStateAzureProvider } from "@ls/grid-state-module-azure";
import { useUser } from "@ls/user-module";
import Logger from "@ls/logger-module";
import LoadingMask from "@ls/react-loading-mask";
import styles from "./App.module.css";
import TopBar from "components/TopBar/TopBar";
import SelectionState from "state/selections/SelectionState";
import Selection from 'components/Selection';
import {
  selectSelectedAsOfDate,
  selectSelectedAsOfDateStart,
  selectModeValue,
  selectSelectedEntityIds,
  selectSelectedGroupTypeValue,
  selectSelectedGroupValues,
  selectRemovedSelectionFiltersDialog,
} from "state/selections/selectors";
import Mode from "state/selections/Mode";
import {
  setSelectedAsOfDate,
  commitSelections,
  requestHoldingAttributes,
  requestIn2Users,
  clearRemovedSelectionFiltersDialog,
  loadEntities,
  loadSelectionSecurityAttributes,
  setSymbolicDate,
  setSymbolicDateStart,
  setGroupType,
  setGroup,
  setSelectedAsOfDateStart,
  setMode,
  setEntityIds,
  setSecurityIdsFilter,
  // addEntityId,
  setIsDisplayRgl,
  setLocalCurrencyFilter,
  setOfferingMarketsFilter,
  setTransactionCodeFilter,
  setReplaceMajorsWithMinors,
  setTransactionEffectFilter,
  setTickerFilter,
  setIsShowCanceledTrades,
  setInstrumentTypesFilter,
  setInstrumentTypesIncludeOption,
  setMsaaFilter,
  setMsaaBucketFilter,
  setTraderIds
} from "state/selections/actions";
import {
  setUsername,
  setAppTitle,
  setAppDecimal,
  setBandedRows,
  setGridVerticalLines,
  setGridFontSize,
  setGridRowHeight,
  toggleDrawer,
  startRequests,
  setHideGridSideBar,
  setNoAccountFlag,
  setDisplayMajorMinorOnTitle
} from "state/app/actions";
import {
  selectAppTitle,
  selectAppDecimal,
  selectIsDrawerOpen,
} from "state/app/selectors";
import { selectDefinitions as selectBetaDefinitions } from 'state/betas/selectors';
import { selectDefinitions as selectHoldingDefinitions } from 'state/holdingAttributes/selectors';
import { setDefinitions as setBetaDefinitions } from 'state/betas/actions';
import { setDefinitions as setHoldingDefinitions } from 'state/holdingAttributes/actions';

import BetaDefinition from 'state/betas/BetaDefinition';
import HoldingAttributeDefinition from 'state/holdingAttributes/HoldingAttributeDefinition';
import ResultGrid from "components/ResultGrid";
import { selectGridStateId } from "state/grid/selector";
import { setGridIdName } from "state/grid/actions";
import { useFetchingTradeHistoryData } from "state/analytics";
import { setDefinitions } from "state/tradehistoryAttributes/actions";
import { useColumnDefinitions } from "state/grid";
import { getPublicContext } from 'tools';
import { useDistributionOptions } from '@ls/distribution';

const getUrlStateId = () => {
  const publicContext = getPublicContext();
  const pathNames = window.location.pathname.split('/').filter(s => s.length);
  if (pathNames?.length > 0) {
    const potentialStateId = decodeURIComponent(pathNames[pathNames.length - 1]);
    if (potentialStateId !== publicContext) {
      return potentialStateId;
    }
  }
};

const App = (props) => {
  const dispatch = useDispatch();
  const prompt = useShowPrompt();
  const [disclaimer, setDisclaimer] = useState(false);
  const drawerOpen = useSelector(selectIsDrawerOpen);
  const fetchingTradeHistoryData = useFetchingTradeHistoryData();
  const uncommittedAsOfDate = useSelector(selectSelectedAsOfDate(SelectionState.UNCOMMITTED));
  const uncommittedAsOfDateStart = useSelector(selectSelectedAsOfDateStart(SelectionState.UNCOMMITTED));
  const uncommittedModeValue = useSelector(selectModeValue(SelectionState.UNCOMMITTED));
  const uncommittedGroupType = useSelector(selectSelectedGroupTypeValue(SelectionState.UNCOMMITTED));
  const uncommittedEntityIds = useSelector(selectSelectedEntityIds(SelectionState.UNCOMMITTED));
  const uncommittedGroups = useSelector(selectSelectedGroupValues(SelectionState.UNCOMMITTED));
  const loadingData = fetchingTradeHistoryData;
  //const getDate = useGetDate();
  const store = useStore();
  const [waitingForSymbolicDate, setWaitingForSymbolicDate] = useState(false);
  const [waitingForSymbolicDateStart, setWaitingForSymbolicDateStart] = useState(false);
  const appTitle = useSelector(selectAppTitle);
  const decimalValue = useSelector(selectAppDecimal);
  const gridStateId = useSelector(selectGridStateId);
  /*{ distribution } Could be deleted, but needs refactoring*/
  const { distribution } = useDistributionOptions();
  const selectionState = useSelector((state) => state.selection[SelectionState.COMMITTED]);
  const betaDefinitions = useSelector(selectBetaDefinitions(SelectionState.COMMITTED));
  const holdingDefinitions = useSelector(selectHoldingDefinitions(SelectionState.COMMITTED));
  const appState = useSelector((state) => state.app);
  const removedSelectionFiltersDialog = useSelector(selectRemovedSelectionFiltersDialog);

  const [auxStateRequest, setAuxStateRequest] = useState(null);

  const auxStateReqSymbolicDateStart = auxStateRequest?.selectionState?.symbolicDateStart;
  const auxStateReqSymbolicDateEnd = auxStateRequest?.selectionState?.symbolicDate;

  const auxStateSymbolicDateStartQuery = useDateQuery(auxStateReqSymbolicDateStart);
  const auxStateSymbolicDateEndQuery = useDateQuery(auxStateReqSymbolicDateEnd);

  const getAuxState = useCallback(() => {
    return {
      selectionState,
      appState,
      betaDefinitions: betaDefinitions.map(definition => definition.copy()),
      holdingDefinitions: holdingDefinitions.map(definition => definition.copy()),
    };
  }, [selectionState, appState, betaDefinitions, holdingDefinitions]);
  const columnDefssss = useColumnDefinitions();
  const GridStateProvider =
    process.env.REACT_APP_HOST_ENV === "AZURE"
      ? GridStateAzureProvider
      : GridStateLegacyProvider;

  const entitiesEnabled =
    uncommittedModeValue === Mode.ENTITIES
    && !!uncommittedAsOfDate
    && !!uncommittedAsOfDateStart;

  useEffect(() => {
    if (!entitiesEnabled) {
      return;
    }

    let canceled = false;
    const query = {
      asOfDate: uncommittedAsOfDate,
      asOfDateStart: uncommittedAsOfDateStart,
      distribution: distribution ?? "",
    };

    dispatch(loadEntities(query, () => canceled));
    //Clean up function to cancel dispatches if the dependencies change before the request completes
    return () => {
      canceled = true;
    };
  }, [
    dispatch,
    uncommittedAsOfDate,
    uncommittedAsOfDateStart,
    distribution,
    entitiesEnabled,
  ]);

  const selectionSecurityIdentifiers = uncommittedModeValue === Mode.ENTITIES ? uncommittedEntityIds : uncommittedGroups;
  const selectionSecurityGroupType = uncommittedModeValue === Mode.ENTITIES ? "entity_list" : uncommittedGroupType?.toLowerCase();
  const selectionSecurityEnabled =
    process.env.REACT_APP_HOST_ENV === "ACE"
    && !!uncommittedAsOfDateStart
    && !!uncommittedAsOfDate
    && !!selectionSecurityGroupType
    && !!selectionSecurityIdentifiers?.length;

  useEffect(() => {
    if (!selectionSecurityEnabled) {
      return;
    }

    let canceled = false;
    const query = {
      asOfDateStart: uncommittedAsOfDateStart,
      asOfDate: uncommittedAsOfDate,
      groupType: selectionSecurityGroupType,
      identifiers: [...selectionSecurityIdentifiers],
    };

    dispatch(loadSelectionSecurityAttributes(query, () => canceled));
    //Clean up function to cancel dispatches if the dependencies change before the request completes
    return () => {
      canceled = true;
    };
  }, [
    dispatch,
    uncommittedAsOfDateStart,
    uncommittedAsOfDate,
    selectionSecurityGroupType,
    selectionSecurityIdentifiers,
    selectionSecurityEnabled,
  ]);

  React.useEffect(() => {
    if (auxStateRequest) {
      let asOfDateStart;
      let asOfDateEnd;

      if (auxStateReqSymbolicDateStart?.length) { //if aux date has symbolic dates, grab the resolved dates from the symbolic date query
        asOfDateStart = auxStateSymbolicDateStartQuery?.data;
      } else {
        asOfDateStart = auxStateRequest.selectionState.asOfDateStart;
      }

      if (auxStateReqSymbolicDateEnd?.length) { //if aux date has symbolic dates, grab the resolved dates from the symbolic date query
        asOfDateEnd = auxStateSymbolicDateEndQuery?.data;
      } else {
        asOfDateEnd = auxStateRequest.selectionState.asOfDate;
      }

      //as of dates maybe null if waiting on symbolic date.
      if (!asOfDateStart || !asOfDateEnd) return;
      const { selectionState, appState, betaDefinitions = [], holdingDefinitions = [] } = auxStateRequest;
      const {
        symbolicDateStart,
        symbolicDate,
        groupType,
        groups,
        mode,
        entityIds,
        isDisplayRgl,
        localCurrencyCodes,
        offeringMarkets,
        transactionCodes,
        transactionEffects,
        tickers,
        isShowCanceledTrades,
        securityIds,
        instrumentTypes,
        instrumentTypesIncludeOption,
        absMsaaSectors,
        msaaBucketCodes,
        traderIds,
        replaceMajorsWithMinors
      } = selectionState;
      batch(async () => {
        dispatch(setAppTitle(appState?.appTitle ?? "Trade History"));
        dispatch(setAppDecimal(appState?.appDecimal ?? 2));
        dispatch(setBandedRows(appState.bandedRows ?? true));
        dispatch(setDisplayMajorMinorOnTitle(appState.displayMajorMinorOnTitle ?? true));
        dispatch(setGridVerticalLines(appState.gridVerticalLines ?? true));
        dispatch(setNoAccountFlag(appState.noAccountLoad ?? false));

        if (new URLSearchParams(window.location.search)?.get('hideGridBarOption') === 'true') {
          dispatch(setHideGridSideBar(true));
        } else {
          dispatch(setHideGridSideBar(appState.hideGridSideBar ?? false));
        }

        dispatch(setGridFontSize(appState.gridFontSize ?? 12));
        dispatch(setGridRowHeight(appState.gridRowHeight ?? 22));

        dispatch(setMode(mode));
        if (!appState.noAccountLoad) {
          dispatch(setGroupType(groupType));
          dispatch(setGroup(groups));
          dispatch(setEntityIds(entityIds));
          dispatch(setSecurityIdsFilter(securityIds))
          dispatch(setIsDisplayRgl(isDisplayRgl));
          dispatch(setLocalCurrencyFilter(localCurrencyCodes));
          dispatch(setOfferingMarketsFilter(offeringMarkets));
          dispatch(setTransactionCodeFilter(transactionCodes));
          dispatch(setReplaceMajorsWithMinors(replaceMajorsWithMinors));
          dispatch(setTransactionEffectFilter(transactionEffects));
          dispatch(setTickerFilter(tickers));
          dispatch(setIsShowCanceledTrades(isShowCanceledTrades))
          dispatch(setInstrumentTypesFilter(instrumentTypes))
          dispatch(setInstrumentTypesIncludeOption(instrumentTypesIncludeOption))
          dispatch(setMsaaFilter(absMsaaSectors))
          dispatch(setMsaaBucketFilter(msaaBucketCodes))
          dispatch(setTraderIds(traderIds))
        }
        dispatch(setDefinitions(columnDefssss));
        dispatch(setBetaDefinitions(betaDefinitions.map(def => new BetaDefinition(def))));
        dispatch(setHoldingDefinitions(holdingDefinitions.map(def => new HoldingAttributeDefinition(def))));

        dispatch(setSymbolicDate(symbolicDate ?? null));
        dispatch(setSelectedAsOfDate(asOfDateEnd));

        dispatch(setSymbolicDateStart(symbolicDateStart));
        dispatch(setSelectedAsOfDateStart(asOfDateStart));

        dispatch(commitSelections());
        if (!appState.noAccountLoad) {
          dispatch(toggleDrawer({ override: false }));
          dispatch(startRequests());
        }
      });
    }
  }, [
    auxStateReqSymbolicDateStart?.length,
    auxStateReqSymbolicDateEnd?.length,
    auxStateRequest,
    auxStateSymbolicDateStartQuery?.data,
    auxStateSymbolicDateEndQuery?.data,
    dispatch
  ])


  /*
   * For getting share url from the list of available grid states.
   */
  const getShareUrl = useCallback((gridStateId) => {
    const pathArr = window.location.pathname.split('/').filter(pathSegment => pathSegment.length);
    const baseIdx = pathArr.findIndex(pathSegment => pathSegment === getPublicContext());
    pathArr.splice(baseIdx + 1, pathArr.length); //path is now reset to x/y/z/tradehistoryui
    if (gridStateId) {
      pathArr.push(encodeURIComponent(gridStateId));
    }
    return `${window.location.origin}/${pathArr.join('/')}${window.location.search}`
  }, []);

  const { username } = useUser();

  React.useEffect(() => {
    dispatch(setUsername(username));
    Logger.setUsername(username);
  }, [username, dispatch]);

  useEffect(() => {
    if (!removedSelectionFiltersDialog) {
      return;
    }
/* Not showing the prompt dialog for now as becasue it could look like an Error message especially when loading a save grid state with filters that are no longer available.
   But keeping the code if we want to easily switch it on in the future. For now, just logging the message to console.
    dispatch(clearRemovedSelectionFiltersDialog());
    prompt({
      title: removedSelectionFiltersDialog.title,
      desc: (
        <span>
          {removedSelectionFiltersDialog.message}
        </span>
      ),
      choices: [
        {
          label: "OK",
          primary: true,
        },
      ],
    });
*/
  console.log("Removed Selection Filters: " + removedSelectionFiltersDialog.title);
  console.log(removedSelectionFiltersDialog.message);
  }, [removedSelectionFiltersDialog])
  //[dispatch, prompt, removedSelectionFiltersDialog]);

  React.useEffect(() => {
    window.document.title = appTitle;
  }, [appTitle]);

  const onChangeStateId = useCallback((gridStateId) => {
    dispatch(setGridIdName(gridStateId));
    /**
     * Update URL based on gridStateId.
     * 1) Reset URL to its base states
     * 2) Append new gridStateId to URL path, if it exists
     * 3) Don't forget any query params (location.search)
     */
    const pathArr = window.location.pathname.split('/').filter(pathSegment => pathSegment.length);
    const baseIdx = pathArr.findIndex(pathSegment => pathSegment === getPublicContext());
    pathArr.splice(baseIdx + 1, pathArr.length); //path is now reset
    if (gridStateId) {
      pathArr.push(encodeURIComponent(gridStateId));
    }

    if (process.env.REACT_APP_IN2_ENV === 'DEV' && false) {
      window.history.pushState(null, null, `/${encodeURIComponent(gridStateId ?? '')}`); //must have leading slash
    } else {
      window.history.pushState(null, null, `/${pathArr.join('/')}${window.location.search}`); //must have leading slash
    }
  }, [dispatch]);

  /*
   * sets default date to PBD if no grid state is chosen
   */
  useEffect(() => {
    dispatch(requestHoldingAttributes())

    process.env.REACT_APP_HOST_ENV === "ACE" ? dispatch(requestIn2Users()) : null;
    
    const urlStateId = getUrlStateId();
    const disclosure = new URLSearchParams(window.location.search).get(
      'disclosure'
    );
    console.log(disclosure)
    console.log( typeof(disclosure))
    setDisclaimer(disclosure === 'true' ? true : false);

    if (urlStateId) {
      dispatch(setGridIdName(urlStateId));
    } else {
      dispatch(setSymbolicDateStart('PBD'));
      dispatch(setSymbolicDate('PBD'))
    }
  }, [dispatch]);

  useEffect(() => {
    const { in2AppTalk } = window;
    if (!in2AppTalk) {
      return;
    }

    const instance = in2AppTalk.getInstance();

    instance.subscribe("*", (sub, msg) => {
      if (typeof msg?.get !== "function") {
        return;
      }

      const entityId =
        msg.get("ACCOUNT_FIDR_ENTITY_ID") ??
        msg.get("ACCOUNT_ENTITY_ID") ??
        msg.get("COMPOSITE_ENTITY_ID") ??
        msg.get("GROUP_ACCOUNT_ENTITY_ID") ??
        msg.get("ACCOUNT_SSI_ID");

      console.log("msg - " + msg)

      var keys = Object.keys(msg);
      keys.forEach(key => {
        console.log(key + '|' + msg[key]);
      });

      console.log("ACCOUNT_FIDR_ENTITY_ID - " + msg.get("ACCOUNT_FIDR_ENTITY_ID"))
      console.log("ACCOUNT_ENTITY_ID - " + msg.get("ACCOUNT_ENTITY_ID"))
      console.log("COMPOSITE_ENTITY_ID - " + msg.get("COMPOSITE_ENTITY_ID"))
      console.log("GROUP_ACCOUNT_ENTITY_ID -" + msg.get("GROUP_ACCOUNT_ENTITY_ID"))
      console.log("ACCOUNT_SSI_ID - " + msg.get("ACCOUNT_SSI_ID"))

      console.log("COMBINED_ACCOUNT_ENTITY_ID - " + msg.get("COMBINED_ACCOUNT_ENTITY_ID"))
      console.log("COMBINED_ACCOUNT_NAME - " + msg.get("COMBINED_ACCOUNT_NAME"))
      console.log("COMBINED_ACCOUNT_MAJOR - " + msg.get("COMBINED_ACCOUNT_MAJOR"))
      console.log("COMBINED_ACCOUNT_SSI_ID - " + msg.get("COMBINED_ACCOUNT_SSI_ID"))
      if (entityId && !msg.get('SECURITY_NAME')) { //we want to ignore security in2 messages
        const accEntityId = Number(entityId);
        batch(async () => {
          dispatch(setMode(Mode.ENTITIES));
          dispatch(setEntityIds([accEntityId]));
          dispatch(commitSelections());
          dispatch(toggleDrawer({ override: false }));
          dispatch(startRequests());

        })
      }
    });

    return () => {
      if (in2AppTalk) {
        instance.unsubscribe("*");
      }
    };
  }
  );
  const disclosureAccpt = () => {
    setDisclaimer(false);
  };
  console.log(
    "APP RENDERING....",
    process.env.REACT_APP_BASE_URL_GRID_STATE_SERVICE
  );
  return (
    <>
      {disclaimer === true ? (
        <Dialog acceptDisclaimer={disclosureAccpt} active={disclaimer} />
      ) : (       
          <GridStateProvider
            appId={process.env.REACT_APP_ID}
           /* username={username}*/
            defaultDecimalValue={decimalValue}
            stateId={gridStateId}
            onChangeStateId={onChangeStateId}
            getAuxState={getAuxState}
            setAuxState={setAuxStateRequest}
            getShareUrl={getShareUrl}
            baseURL={
              process.env.REACT_APP_HOST_ENV === "AZURE"
                ? process.env.REACT_APP_BASE_URL_GRID_STATE_SERVICE
                : undefined
            }
            environment={
              process.env.REACT_APP_HOST_ENV !== "AZURE"
                ? process.env.REACT_APP_IN2_ENV
                : undefined
            }
          >
            <div className={styles.app}>
              <Selection />
              <div className={styles.top}>
                <TopBar />
              </div>
              <div className={styles.content}>
                <ResultGrid />
              </div>
              <LoadingMask
                active={loadingData || drawerOpen}
                zIndex={drawerOpen ? 7 : 12}
                showMsg={loadingData}
                showSpinner={loadingData}
              />
            </div>
          </GridStateProvider>
        
      )}
    </>
  );
};

export default App;
